import { useState } from 'react';
import { Image, Upload, FolderOpen, X } from 'lucide-react';
import type { ImageFolder } from '../../types/images';
import ImageManager from './ImageManager';
import ImageUpload from './ImageUpload';
import Button from '../../components/Button';

interface ImagePickerProps {
    value: string;
    onChange: (url: string) => void;
    folder: ImageFolder;
    label?: string;
    placeholder?: string;
    className?: string;
    required?: boolean;
    allowUrlInput?: boolean; // Allow manual URL entry as fallback
    backgroundColor?: string; // New prop for background color
    height?: string; // Height class (e.g., "h-32", "h-48")
    aspectRatio?: string; // Aspect ratio class (e.g., "aspect-video", "aspect-square")
    showUrlDisplay?: boolean; // Whether to show the URL at the bottom
}

export default function ImagePicker({
    value,
    onChange,
    folder,
    label,
    placeholder = "No image selected",
    className = "",
    required = false,
    allowUrlInput = true,
    backgroundColor,
    height = "h-48", // Default height
    aspectRatio = "aspect-video", // Default aspect ratio
    showUrlDisplay = true // Default to show URL
}: ImagePickerProps) {
    const [showManager, setShowManager] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [urlInputValue, setUrlInputValue] = useState('');

    const handleImageSelect = (url: string) => {
        onChange(url);
        setShowManager(false);
    };

    const handleUploadSuccess = (url: string) => {
        onChange(url);
        setShowUpload(false);
    };

    const handleRemoveImage = () => {
        onChange('');
    };

    const handleUrlSubmit = () => {
        if (urlInputValue.trim()) {
            onChange(urlInputValue.trim());
            setUrlInputValue('');
            setShowUrlInput(false);
        }
    };

    const handleUrlInputKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleUrlSubmit();
        } else if (e.key === 'Escape') {
            setUrlInputValue('');
            setShowUrlInput(false);
        }
    };

    return (
        <div className={className}>
            {/* Label */}
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            {/* Current Image Display */}
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex flex-col justify-center">
                {value ? (
                    <div className="relative flex flex-col justify-center group">
                        {/* Image Preview with Background Color */}
                        <div
                            className={`${aspectRatio} ${height} flex items-center justify-center p-2`}
                            style={{
                                backgroundColor: backgroundColor || '#f9fafb'
                            }}
                        >
                            <img
                                src={value}
                                alt="Selected image"
                                className="max-w-full max-h-full object-contain rounded"
                                onError={(e) => {
                                    // If image fails to load, show broken image icon
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    target.parentElement!.innerHTML = `
                    <div class="flex flex-col items-center justify-center text-gray-400">
                      <svg class="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <span class="text-xs">Image failed to load</span>
                    </div>
                  `;
                                }}
                            />
                        </div>

                        {/* Remove Button */}
                        <Button
                            type="button"
                            onClick={handleRemoveImage}
                            variant="danger"
                            className="absolute top-2 right-2 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            title="Remove image"
                            icon={<X size={14} />}
                        />

                        {/* Image URL Display - Only show if not compact */}
                        {showUrlDisplay && (
                            <div className="p-3 bg-gray-100 border-t border-gray-200">
                                <p className="text-xs text-gray-600 truncate" title={value}>
                                    {value}
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Empty State */
                    <div
                        className={`${aspectRatio} ${height} flex flex-col items-center justify-center p-2 text-gray-400`}
                        style={{
                            backgroundColor: backgroundColor || '#f9fafb'
                        }}
                    >
                        <Image size={48} className="mb-2" />
                        <p className="text-sm text-center">{placeholder}</p>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="mt-3 flex gap-2">
                <Button
                    type="button"
                    onClick={() => setShowManager(true)}
                    variant="primary"
                    size="sm"
                    fullWidth
                    icon={<FolderOpen size={14} />}
                >
                    Browse
                </Button>

                <Button
                    type="button"
                    onClick={() => setShowUpload(true)}
                    variant="success"
                    size="sm"
                    fullWidth
                    icon={<Upload size={14} />}
                >
                    Upload
                </Button>

                {allowUrlInput && (
                    <Button
                        type="button"
                        onClick={() => setShowUrlInput(true)}
                        variant="outline"
                        size="sm"
                        title="Enter URL manually"
                    >
                        URL
                    </Button>
                )}
            </div>

            {/* URL Input Field */}
            {showUrlInput && (
                <div className="mt-3 p-3 border border-gray-200 rounded-md bg-gray-50">
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                        Enter Image URL
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={urlInputValue}
                            onChange={(e) => setUrlInputValue(e.target.value)}
                            onKeyDown={handleUrlInputKeyPress}
                            placeholder="https://example.com/image.jpg"
                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            autoFocus
                        />
                        <Button
                            type="button"
                            onClick={handleUrlSubmit}
                            disabled={!urlInputValue.trim()}
                            variant="primary"
                            size="sm"
                        >
                            Add
                        </Button>
                        <Button
                            type="button"
                            onClick={() => {
                                setUrlInputValue('');
                                setShowUrlInput(false);
                            }}
                            variant="outline"
                            size="sm"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            {/* Image Manager Modal */}
            <ImageManager
                isOpen={showManager}
                onClose={() => setShowManager(false)}
                onSelectImage={handleImageSelect}
                currentFolder={folder}
                title={`Select Image for ${folder.charAt(0).toUpperCase() + folder.slice(1)}`}
            />

            {/* Upload Modal */}
            {showUpload && (
                <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-[60] px-4 py-6 sm:py-20">
                    <div className="relative mx-auto p-0 border w-full max-w-lg shadow-lg rounded-md bg-white">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">
                                Upload Image to {folder.charAt(0).toUpperCase() + folder.slice(1)}
                            </h3>
                            <Button
                                onClick={() => setShowUpload(false)}
                                variant="ghost"
                                icon={<X size={24} />}
                            />
                        </div>

                        {/* Upload Component */}
                        <div className="p-4 sm:p-6">
                            <ImageUpload
                                folder={folder}
                                onUploadSuccess={handleUploadSuccess}
                                onUploadError={(error) => console.error('Upload error:', error)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
} 